/**
 * <mobile-configurator> — Gee Brothers mobile product configurator
 * ---------------------------------------------------------------------------
 * A presentation layer over the personalised product form. It owns no state of
 * its own: every value is read from, and written back to, the real controls in
 * <form data-pp-form>, by setting `select.value` and dispatching a bubbling
 * `change` — exactly the route the section's own swatch modal already uses. So
 * pricing, the live mock-up, the bundle builder and the cart post are all
 * unchanged, and a cart line built here is byte-identical to one built from the
 * dropdowns.
 *
 * Only active below 1000px (where the section collapses to one column). Above
 * that the element hides itself and hands everything back to the form.
 *
 * Vanilla JS, no framework, matching the section it enhances.
 */

const MOBILE = '(max-width: 999px)';
const AUTO_DISMISS_MS = 250;

const CHECK_SVG =
  '<svg class="gbc__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="M3 8.5l3.5 3.5L13 4.5"/></svg>';

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

/**
 * Is this control currently offered to the customer? The section hides options
 * three different ways (a .pp__hidden wrapper, a [hidden] group, an inline
 * display:none) and disables selects it cannot price, so check all of them.
 * Deliberately ignores our own CSS, which hides the whole form on mobile.
 */
function usable(el) {
  if (!el || el.disabled) return false;
  let node = el;
  while (node && node.nodeType === 1 && node.tagName !== 'PRODUCT-PERSONALISED') {
    if (node.classList.contains('pp__hidden')) return false;
    if (node.hasAttribute('hidden')) return false;
    if (node.style && node.style.display === 'none') return false;
    node = node.parentElement;
  }
  return true;
}

/** The options a customer may actually pick (the section disables the rest). */
function liveOptions(select) {
  return Array.prototype.filter.call(select.options, (o) => !o.disabled && !o.hidden);
}

/** The <label> the section already wrote for this control — already translated. */
function labelFor(el, fallback) {
  const group = el.classList.contains('pp__group') ? el : el.closest('.pp__group');
  const label = group && group.querySelector('label');
  return label ? label.textContent.trim() : fallback || '';
}

class MobileConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.root = this.closest('product-personalised');
    this.form = this.root && this.root.querySelector('[data-pp-form]');
    if (!this.root || !this.form) return;

    this.initialised = true;
    // Which steps the customer has actually chosen, as opposed to left on their
    // default. Drives the pills' confirmed state. Session-only: a reload puts
    // the form back to its defaults, so the ribbon should reset with it.
    this.confirmed = new Set();

    try {
      this.strings = this.readStrings();
      this.cacheNodes();
      this.buildAttributes();
      this.bind();
    } catch (err) {
      this.hidden = true;
      this.root.removeAttribute('data-gbc');
      console.warn('[mobile-configurator] could not start, the options form stays visible:', err);
      return;
    }

    this.mq = window.matchMedia(MOBILE);
    this.onBreakpoint = () => (this.mq.matches ? this.enable() : this.disable());
    this.mq.addEventListener('change', this.onBreakpoint);
    this.onBreakpoint();
  }

  disconnectedCallback() {
    if (this.mq) this.mq.removeEventListener('change', this.onBreakpoint);
    if (this.priceObserver) this.priceObserver.disconnect();
    if (this.stateObserver) this.stateObserver.disconnect();
  }

  readStrings() {
    const el = this.querySelector('[data-gbc-strings]');
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return {};
    }
  }

  cacheNodes() {
    const root = this.root;
    this.gallery = root.querySelector('[data-gallery]');
    this.galleryMain = root.querySelector('[data-gallery-main]');
    this.formHead = root.querySelector('.pp__form-head');
    this.priceEl = root.querySelector('[data-pp-price]');
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.noteEl = root.querySelector('[data-pp-note]');
    this.estimateEl = root.querySelector('[data-pp-estimate]');
    this.envNoteEl = root.querySelector('[data-pp-env-note]');
    this.wordingFormat = root.querySelector('[data-pp-wformat]');
    this.wordingText = root.querySelector('[data-pp-prop="Wording"]');
    this.wordingFile = root.querySelector('[data-pp-file]');

    this.lineCard = this.querySelector('[data-gbc-line="card"]');
    this.lineEnv = this.querySelector('[data-gbc-line="env"]');
    this.groupCard = this.querySelector('[data-gbc-group="card"]');
    this.groupEnv = this.querySelector('[data-gbc-group="env"]');
    this.head = this.querySelector('[data-gbc-head]');
    this.note = this.querySelector('[data-gbc-note]');

    this.scrim = this.querySelector('[data-gbc-scrim]');
    this.sheet = this.querySelector('[data-gbc-sheet]');
    this.sheetTitle = this.querySelector('[data-gbc-sheet-title]');
    this.sheetBody = this.querySelector('[data-gbc-sheet-body]');
    this.sheetHint = this.querySelector('[data-gbc-sheet-hint]');
    this.grab = this.querySelector('[data-gbc-grab]');

    this.takeover = this.querySelector('[data-gbc-wording]');
    this.wPreview = this.querySelector('[data-gbc-wording-preview-text]');
    this.wFormats = this.querySelector('[data-gbc-wording-formats]');
    this.wField = this.querySelector('[data-gbc-wording-field]');
    this.wInput = this.querySelector('[data-gbc-wording-input]');
    this.wFileRow = this.querySelector('[data-gbc-wording-file]');
    this.wFileName = this.querySelector('[data-gbc-wording-file-name]');
    this.pencil = root.querySelector('[data-gbc-wording-open]');

    this.bar = this.querySelector('[data-gbc-bar]');
    this.barPrice = this.querySelector('[data-gbc-bar-price]');
    this.barAmount = this.querySelector('[data-gbc-bar-amount]');
    this.barError = this.querySelector('[data-gbc-bar-error]');
    this.addBtn = this.querySelector('[data-gbc-add]');
    this.remaining = this.querySelector('[data-gbc-remaining]');
    this.addLabel = this.querySelector('[data-gbc-add-label]');
  }

  /**
   * Map every spec-line value to the control that owns it. Order here is the
   * order they read in the line. Anything the product does not offer simply
   * has no element and drops out.
   */
  buildAttributes() {
    const root = this.root;
    const q = (sel) => root.querySelector(sel);

    // Process and card quantity are driven through selects the section itself
    // hides (it replaced them with a card picker and a stepper), so their
    // availability and label come from the visible group instead — scopeEl.
    const processTrigger = q('[data-pp-process-open]');

    this.specAttrs = [
      // — Your card ——————————————————————————————————————————————
      {
        key: 'process',
        group: 'card',
        el: q('[data-pp-process-select]'),
        scopeEl: processTrigger && processTrigger.closest('.pp__group'),
        // Opens the section's own process picker: it carries the process
        // photography and live price deltas, which a sheet would have to fork.
        delegate: '[data-pp-process-open]'
      },
      { key: 'ink', group: 'card', el: q('[data-pp-ink]'), type: 'swatch' },
      { key: 'paper', group: 'card', el: q('[data-pp-paper]'), type: 'swatch' },
      { key: 'finish', group: 'card', el: q('[data-pp-finish]'), type: 'texture' },
      { key: 'style', group: 'card', el: q('[data-pp-style]'), type: 'specimen' },
      { key: 'border', group: 'card', el: q('[data-pp-border-include]'), noneLabel: 'none_border' },
      { key: 'borderColour', group: 'card', el: q('[data-pp-border]'), type: 'swatch' },
      {
        key: 'cardQty',
        group: 'card',
        el: q('[data-pp-qty-select]'),
        scopeEl: q('[data-pp-cardqty-group]'),
        type: 'stepper',
        // Proxy the section's own stepper buttons so the envelope default and
        // the QTY-break pricing follow, exactly as on desktop.
        dec: '[data-pp-qty-dec]',
        inc: '[data-pp-qty-inc]'
      },

      // — Envelopes ——————————————————————————————————————————————
      { key: 'envQty', group: 'env', el: q('[data-pp-envqty]'), type: 'stepper', hint: true },
      { key: 'lining', group: 'env', el: q('[data-pp-lining]'), type: 'swatch', noneLabel: 'none_lining' },
      { key: 'return', group: 'env', el: q('[data-pp-return]'), noneLabel: 'none_return' },
      { key: 'blanks', group: 'env', el: q('[data-pp-blankqty]'), type: 'stepper', noneLabel: 'none_blanks' }
    ].filter((attr) => attr.el);

    // Everything else the section offers. The list above is an allowlist, and an
    // allowlist silently drops whatever it has not heard of — which it did:
    // guide lines, motif and monogram were unreachable on mobile, because the
    // form that holds them is hidden and nothing here replaced them. Sweeping
    // the form means any option the section gains in future turns up on its own.
    const mapped = new Set(this.specAttrs.map((a) => a.el));
    this.form.querySelectorAll('[data-pp-prop]').forEach((el) => {
      if (mapped.has(el)) return;
      const prop = el.getAttribute('data-pp-prop');
      if (prop === 'Wording' || prop === 'Wording format') return; // the takeover owns these
      const isSelect = el.tagName === 'SELECT';
      const isText = el.tagName === 'INPUT' && el.type === 'text';
      if (!isSelect && !isText) return;
      this.specAttrs.push({
        key: `prop-${prop.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        group: 'card',
        el,
        type: isText ? 'text' : undefined
      });
    });

    this.specAttrs.forEach((attr) => {
      attr.scopeEl = attr.scopeEl || attr.el;
      attr.label = labelFor(attr.scopeEl, attr.el.getAttribute('data-pp-prop'));
    });
  }

  bind() {
    // Any change to the form re-renders the line. recalcTotal() always rewrites
    // the price, so watching it also catches the changes that never fire a
    // `change` event (the section's own qty stepper buttons, for one).
    // A change on a mapped control means the customer chose that step — which
    // also covers the process picker, whose modal is the section's own.
    // Availability updates (the section rewriting a select it has re-gated)
    // set .value directly without dispatching, so they never false-confirm.
    this.onFormChange = (e) => {
      const attr = this.specAttrs.find((a) => a.el === e.target);
      if (attr) this.confirmed.add(attr.key);
      this.scheduleRender();
    };
    this.form.addEventListener('change', this.onFormChange);
    this.form.addEventListener('input', this.onFormChange);

    if (this.priceEl) {
      this.priceObserver = new MutationObserver(() => this.scheduleRender());
      this.priceObserver.observe(this.priceEl, { childList: true, characterData: true, subtree: true });
    }

    // The section toggles groups (return address, paper finish, border colour)
    // as availability changes; mirror those into the line.
    this.stateObserver = new MutationObserver(() => this.scheduleRender());
    this.stateObserver.observe(this.form, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'hidden', 'disabled']
    });

    this.querySelector('[data-gbc-spec]').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gbc-open]');
      if (!btn) return;
      this.revealPill(btn);
      this.open(btn.getAttribute('data-gbc-open'), btn);
    });

    this.scrim.addEventListener('click', () => this.closeSheet());
    this.sheetBody.addEventListener('click', (e) => this.onRowClick(e));
    this.sheetBody.addEventListener('keydown', (e) => this.onRowKeydown(e));
    this.sheet.addEventListener('keydown', (e) => this.onDialogKeydown(e));
    this.takeover.addEventListener('keydown', (e) => this.onDialogKeydown(e));
    this.bindSwipe();

    if (this.pencil) this.pencil.addEventListener('click', (e) => this.openWording(e.currentTarget));
    this.querySelector('[data-gbc-wording-close]').addEventListener('click', () => this.closeWording());
    this.querySelector('[data-gbc-wording-done]').addEventListener('click', () => this.closeWording());
    this.wFormats.addEventListener('click', (e) => this.onWordingFormatClick(e));
    this.wInput.addEventListener('input', () => this.onWordingInput());
    this.querySelector('[data-gbc-wording-file-btn]').addEventListener('click', () => {
      if (this.wordingFile) this.wordingFile.click();
    });
    if (this.wordingFile) {
      this.wordingFile.addEventListener('change', () => this.syncWordingFile());
    }

    this.addBtn.addEventListener('click', () => this.addToCart());
    this.remaining.addEventListener('click', (e) => {
      if (e.target === this.remaining || e.target.closest('[data-gbc-remaining-close]')) this.closeRemaining();
    });
    this.remaining.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeRemaining();
    });

    // Mirror the form's own add-to-cart feedback into the bar.
    if (this.submitBtn) {
      this.busyObserver = new MutationObserver(() => this.syncBusy());
      this.busyObserver.observe(this.submitBtn, { attributes: true, attributeFilter: ['disabled'] });
    }
    if (this.noteEl) {
      this.noteObserver = new MutationObserver(() => this.syncError());
      this.noteObserver.observe(this.noteEl, { attributes: true, childList: true, characterData: true, subtree: true });
    }

    if (window.visualViewport) {
      this.onViewport = () => this.syncKeyboardInset();
      window.visualViewport.addEventListener('resize', this.onViewport);
      window.visualViewport.addEventListener('scroll', this.onViewport);
    }
  }

  /* ------------------------------------------------------------- lifecycle */

  /**
   * Take over — but only if we can prove we have something to take over with.
   *
   * data-gbc="on" is what hides the dropdown form, so it is set LAST, after a
   * spec line has actually rendered. Anything that goes wrong before that point
   * leaves the form on screen: a page that shifts is a nuisance, a page with no
   * way to choose or buy is a broken shop.
   */
  enable() {
    const drivable = this.specAttrs.filter((a) => usable(a.scopeEl) && !a.el.disabled);
    if (!drivable.length) return this.standDown('no configurable options found on this product');

    try {
      this.hidden = false; // still invisible: the CSS reveals it with data-gbc="on"
      this.render();
      if (!this.querySelector('[data-gbc-line] [data-gbc-open]')) {
        throw new Error('the spec line rendered empty');
      }

      // Move the title under the render so the page still names itself above
      // the fold. Remembered so disable() can put it back untouched.
      if (this.formHead && this.formHead.parentElement !== this.head) {
        this.headHome = { parent: this.formHead.parentElement, next: this.formHead.nextSibling };
        this.head.appendChild(this.formHead);
      }

      if (this.estimateEl && this.note && !this.note.firstElementChild) {
        const copy = this.estimateEl.cloneNode(true);
        copy.removeAttribute('data-pp-estimate');
        copy.removeAttribute('style');
        this.note.appendChild(copy);
        this.note.hidden = false;
      }

      if (this.submitBtn) this.syncBusy();
      if (this.noteEl) this.syncError();
      // No wording control (a fixed surname/monogram design) means no takeover to
      // open, so the pencil over the render would be a dead affordance.
      if (this.pencil) this.pencil.hidden = !this.wordingFormat;

      this.root.setAttribute('data-gbc', 'on');
    } catch (err) {
      this.standDown(err);
    }
  }

  /** Hand the page back to the dropdown form and say why. */
  standDown(reason) {
    this.disable();
    console.warn('[mobile-configurator] standing down, the options form stays visible:', reason);
  }

  disable() {
    this.closeSheet(true);
    this.closeWording(true);
    this.root.removeAttribute('data-gbc');
    this.hidden = true;
    if (this.pencil) this.pencil.hidden = true;
    this.restoreHead();
  }

  restoreHead() {
    if (!this.headHome || !this.formHead) return;
    this.headHome.parent.insertBefore(this.formHead, this.headHome.next);
    this.headHome = null;
  }

  scheduleRender() {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      if (this.mq.matches) this.render();
    });
  }

  /* ------------------------------------------------------------- spec line */

  render() {
    this.renderGroup('card', this.groupCard, this.lineCard);
    this.renderGroup('env', this.groupEnv, this.lineEnv);
    this.syncPrice();
    if (this.sheetAttr) this.renderSheetBody(this.sheetAttr);
    if (!this.takeover.hidden) this.syncWordingPreview();
  }

  renderGroup(group, wrapper, line) {
    const attrs = this.specAttrs.filter((a) => a.group === group && usable(a.scopeEl) && !a.el.disabled);
    const parts = attrs.map((attr) => this.pill(attr));

    // Wording is a step like any other, so it rides in the ribbon too — amber
    // while it is outstanding, navy once there is something to print.
    if (group === 'card' && this.wordingFormat) parts.push(this.wordingPill());

    wrapper.hidden = parts.length === 0;
    const next = parts.join('');
    if (line.dataset.html === next) return;

    // Rebuilding resets the scroller, which would yank the ribbon back to the
    // start every time the customer changes something at the far end.
    const scroll = line.scrollLeft;
    const firstPaint = line.dataset.html === undefined;
    const previous = {};
    line.querySelectorAll('[data-gbc-open]').forEach((b) => {
      previous[b.getAttribute('data-gbc-open')] = b.dataset.value;
    });

    line.dataset.html = next;
    line.innerHTML = next;
    line.scrollLeft = scroll;

    if (firstPaint) return;
    line.querySelectorAll('[data-gbc-open]').forEach((b) => {
      const key = b.getAttribute('data-gbc-open');
      if (key in previous && previous[key] !== b.dataset.value) b.classList.add('is-updated');
    });
  }

  /**
   * One step of the configuration. Outlined and muted while it still holds its
   * default; filled navy with a tick once the customer has chosen it, so the
   * ribbon reads as progress at a glance.
   */
  pill(attr) {
    const value = this.displayValue(attr);
    const confirmed = this.confirmed.has(attr.key);
    let aria = (this.strings.change_aria || '__ATTRIBUTE__, __VALUE__, change')
      .replace('__ATTRIBUTE__', attr.label)
      .replace('__VALUE__', value);
    if (confirmed) aria += `, ${this.strings.confirmed_aria || 'chosen'}`;

    return this.pillMarkup({
      key: attr.key,
      label: attr.label,
      value,
      aria,
      confirmed,
      swatch: this.pillSwatch(attr)
    });
  }

  wordingPill() {
    const missing = this.missingWording();
    const value = missing || this.wordingSummary();
    const label = this.strings.wording || 'Wording';
    return this.pillMarkup({
      key: 'wording',
      label,
      value,
      aria: `${label}, ${value}, change`,
      confirmed: !missing,
      required: !!missing
    });
  }

  /** What the wording pill shows once there is something to print. */
  wordingSummary() {
    const format = this.wordingFormat.value;
    if (format === 'Upload file') {
      const file = this.wordingFile && this.wordingFile.files && this.wordingFile.files[0];
      return file ? file.name : this.strings.wording_added || 'Added';
    }
    if (format === 'Complete your wording' && this.wordingText) {
      const firstLine = this.wordingText.value.split('\n').find((l) => l.trim());
      if (firstLine) {
        const clean = firstLine.trim();
        return clean.length > 24 ? `${clean.slice(0, 23)}…` : clean;
      }
    }
    // "Wording provided separately" and anything else: show the chosen format.
    const option = this.wordingFormat.options[this.wordingFormat.selectedIndex];
    return option ? option.textContent.trim() : this.strings.wording_added || 'Added';
  }

  pillMarkup({ key, label, value, aria, confirmed, required, swatch }) {
    const outstanding = !confirmed && !!(this.outstandingKeys && this.outstandingKeys.has(key));
    const classes = [
      'gbc__pill',
      confirmed ? 'is-confirmed' : '',
      required ? 'gbc__pill--required' : '',
      outstanding ? 'is-outstanding' : ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      `<button type="button" class="${classes}" data-gbc-open="${escapeHtml(key)}"` +
      ` data-value="${escapeHtml(value)}" aria-label="${escapeHtml(aria)}">` +
      (swatch || '') +
      '<span class="gbc__pill-text">' +
      `<span class="gbc__pill-label">${escapeHtml(label)}</span>` +
      `<span class="gbc__pill-value">${escapeHtml(value)}</span>` +
      '</span>' +
      (confirmed ? CHECK_SVG : '') +
      '</button>'
    );
  }

  /** Colour steps carry their colour, so the pill is identifiable at a glance. */
  pillSwatch(attr) {
    if (attr.type !== 'swatch' || !attr.el.options) return '';
    const option = attr.el.options[attr.el.selectedIndex];
    const hex = option ? (option.getAttribute('data-hex') || '').trim() : '';
    const none = !hex || hex === '-' || this.isNone(attr);
    return `<span class="gbc__dot${none ? ' gbc__dot--none' : ''}" style="--gbc-swatch:${escapeHtml(
      none ? 'transparent' : hex
    )}" aria-hidden="true"></span>`;
  }

  isNone(attr) {
    const v = attr.el.value;
    return v === 'None' || v === '' || v === '0';
  }

  /** What the line shows. "None" reads as plain English, not as a form value. */
  displayValue(attr) {
    const select = attr.el;
    if (this.isNone(attr) && attr.noneLabel && this.strings[attr.noneLabel]) {
      return this.strings[attr.noneLabel];
    }
    // A text field, e.g. the monogram. Empty reads as "None" rather than as a
    // blank pill, which looks like a rendering fault.
    if (!select.options) return select.value.trim() || this.strings.none || 'None';
    const option = select.options[select.selectedIndex];
    return option ? option.textContent.trim() : '';
  }

  /**
   * Wording is outstanding when the customer chose to type it and has not, or
   * chose to upload and has not. Returns the placeholder text, or null.
   */
  missingWording() {
    if (!this.wordingFormat) return null;
    const format = this.wordingFormat.value;
    if (format === 'Complete your wording') {
      const empty = !this.wordingText || !this.wordingText.value.trim();
      return empty ? this.strings.wording_required : null;
    }
    if (format === 'Upload file') {
      const chosen = this.wordingFile && this.wordingFile.files && this.wordingFile.files.length > 0;
      return chosen ? null : this.strings.wording_file_required;
    }
    return null;
  }

  /* ----------------------------------------------------------------- sheet */

  open(key, trigger) {
    if (key === 'wording') return this.openWording(trigger);

    const attr = this.specAttrs.find((a) => a.key === key);
    if (!attr) return;

    if (attr.delegate) {
      // Hand straight to the section's own picker.
      const btn = this.root.querySelector(attr.delegate);
      if (btn) btn.click();
      return;
    }

    this.rememberFocus(trigger);
    this.sheetAttr = attr;
    this.sheetSig = null;
    this.sheetTitle.textContent = attr.label;
    this.renderSheetBody(attr);

    const hint = attr.hint && this.envNoteEl ? this.envNoteEl.textContent.trim() : '';
    this.sheetHint.textContent = hint;
    this.sheetHint.hidden = !hint;

    window.clearTimeout(this.hideSheetTimer);
    this.scrim.hidden = false;
    this.sheet.hidden = false;
    this.lockScroll(true);
    requestAnimationFrame(() => {
      this.scrim.classList.add('is-open');
      this.sheet.classList.add('is-open');
      this.root.classList.add('gbc-sheet-open');
      const first = this.sheetBody.querySelector('[tabindex="0"], button:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    });
  }

  /**
   * Rebuild the sheet's contents. Every selection re-renders, so remember where
   * the keyboard was and put it back — otherwise focus escapes the dialog.
   */
  renderSheetBody(attr) {
    // Skip the rebuild when nothing this sheet shows has moved — it would only
    // throw away the list's scroll position and the keyboard's place.
    // A text sheet is never rebuilt while it is open: re-rendering it would
    // throw away the caret mid-word.
    if (attr.type === 'text') {
      if (!this.sheetBody.querySelector('[data-gbc-text]')) this.renderTextField(attr);
      return;
    }

    const signature = [
      attr.el.value,
      attr.el.options ? liveOptions(attr.el).map((o) => o.value).join(',') : '',
      attr.type === 'texture' ? this.cssVar('--pp-paper') : ''
    ].join('|');
    if (signature === this.sheetSig) return;
    this.sheetSig = signature;

    const active = document.activeElement;
    const mark = this.sheetBody.contains(active)
      ? active.getAttribute('data-value') || active.getAttribute('data-gbc-step')
      : null;

    if (attr.type === 'stepper') this.renderStepper(attr);
    else this.renderRows(attr);

    if (mark == null) return;
    const restored = this.sheetBody.querySelector(
      `[data-value="${CSS.escape(mark)}"], [data-gbc-step="${CSS.escape(mark)}"]`
    );
    if (restored && !restored.disabled) restored.focus();
  }

  /**
   * A free-text step (the monogram). Types straight through to the real input
   * and echoes back whatever the section makes of it — it forces letters only
   * and uppercase, and keeps its own character count — so the two never differ.
   */
  renderTextField(attr) {
    const max = attr.el.getAttribute('maxlength');
    this.sheetBody.innerHTML =
      '<div class="gbc__textfield">' +
      `<input type="text" class="gbc__text-input" data-gbc-text${max ? ` maxlength="${escapeHtml(max)}"` : ''}` +
      ` value="${escapeHtml(attr.el.value)}" aria-label="${escapeHtml(attr.label)}">` +
      '</div>';

    const field = this.sheetBody.querySelector('[data-gbc-text]');
    field.addEventListener('input', () => {
      attr.el.value = field.value;
      attr.el.dispatchEvent(new Event('input', { bubbles: true }));
      if (attr.el.value !== field.value) field.value = attr.el.value; // section normalised it
      this.confirmed.add(attr.key);
      this.scheduleRender();
    });
    field.focus({ preventScroll: true });
  }

  renderRows(attr) {
    const select = attr.el;
    const rows = liveOptions(select)
      .map((option) => {
        const selected = option.value === select.value;
        return (
          `<button type="button" role="radio" aria-checked="${selected}" tabindex="${selected ? 0 : -1}"` +
          ` class="gbc__row${selected ? ' is-selected' : ''}${attr.type === 'specimen' ? ' gbc__row--specimen' : ''}"` +
          ` data-value="${escapeHtml(option.value)}"${this.specimenStyle(attr, option)}>` +
          `<span class="gbc__row-name">${escapeHtml(option.textContent.trim())}</span>` +
          this.rowSwatch(attr, option) +
          CHECK_SVG +
          '</button>'
        );
      })
      .join('');

    this.sheetBody.innerHTML = `<div class="gbc__rows" role="radiogroup" aria-labelledby="gbc-sheet-title">${rows}</div>`;
  }

  specimenStyle(attr, option) {
    if (attr.type !== 'specimen') return '';
    const font = option.getAttribute('data-font');
    const transform = option.getAttribute('data-transform') || 'none';
    if (!font) return '';
    return ` style="--gbc-specimen:${escapeHtml(font)};--gbc-specimen-transform:${escapeHtml(transform)}"`;
  }

  rowSwatch(attr, option) {
    if (attr.type === 'swatch') {
      const hex = (option.getAttribute('data-hex') || '').trim();
      const none = !hex || hex === '-' || option.value === 'None';
      return `<span class="gbc__dot${none ? ' gbc__dot--none' : ''}" style="--gbc-swatch:${escapeHtml(
        none ? 'transparent' : hex
      )}" aria-hidden="true"></span>`;
    }
    if (attr.type === 'texture') {
      // Reuse the section's own finish swatch — the wove chip and the laid
      // ribbing are already drawn and already track the paper colour. Drawing a
      // second version here would be two things to keep in step.
      const laid = /laid/i.test(option.value) ? ' pp__finish-swatch--laid' : '';
      const paper = this.cssVar('--pp-paper') || '#f4f1ea';
      return `<span class="pp__finish-swatch${laid} gbc__tile" style="--finish-paper:${escapeHtml(
        paper
      )}" aria-hidden="true"></span>`;
    }
    return '';
  }

  /**
   * Quantities step through the select's still-enabled options, so the
   * section's own floors (minimum envelopes, and the higher floor once a return
   * address is included) are honoured without being restated here.
   */
  renderStepper(attr) {
    const select = attr.el;
    const options = liveOptions(select);
    const index = options.findIndex((o) => o.value === select.value);
    const current = options[index] || select.options[select.selectedIndex];

    this.sheetBody.innerHTML =
      '<div class="gbc__stepper">' +
      `<button type="button" class="gbc__step" data-gbc-step="-1" aria-label="${escapeHtml(
        this.strings.fewer || 'Fewer'
      )}"${index <= 0 ? ' disabled' : ''}>&minus;</button>` +
      `<span class="gbc__step-value" aria-live="polite">${escapeHtml(
        current ? current.textContent.trim() : ''
      )}</span>` +
      `<button type="button" class="gbc__step" data-gbc-step="1" aria-label="${escapeHtml(
        this.strings.more || 'More'
      )}"${index >= options.length - 1 ? ' disabled' : ''}>+</button>` +
      '</div>';
  }

  onRowClick(e) {
    const step = e.target.closest('[data-gbc-step]');
    if (step) return this.step(parseInt(step.getAttribute('data-gbc-step'), 10));

    const row = e.target.closest('[data-value]');
    if (!row || !this.sheetAttr) return;
    this.select(this.sheetAttr, row.getAttribute('data-value'));
    // Confirm the choice on screen, then get out of the way.
    window.setTimeout(() => this.closeSheet(), AUTO_DISMISS_MS);
  }

  onRowKeydown(e) {
    const rows = Array.prototype.slice.call(this.sheetBody.querySelectorAll('[role="radio"]'));
    if (!rows.length) return;
    const index = rows.indexOf(document.activeElement);
    if (index === -1) return;
    let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = rows[(index + 1) % rows.length];
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = rows[(index - 1 + rows.length) % rows.length];
    if (!next) return;
    e.preventDefault();
    next.focus();
    this.select(this.sheetAttr, next.getAttribute('data-value'));
  }

  step(direction) {
    const attr = this.sheetAttr;
    if (!attr || !attr.el.options) return;

    this.confirmed.add(attr.key);

    if (attr.dec && attr.inc) {
      // The section's own stepper: click it so its side effects run too. Those
      // buttons drive the select directly and fire no change event, so the
      // confirmation above is the only one this step will get.
      const btn = this.root.querySelector(direction < 0 ? attr.dec : attr.inc);
      if (btn && !btn.disabled) btn.click();
      this.render();
      return;
    }

    const options = liveOptions(attr.el);
    const index = options.findIndex((o) => o.value === attr.el.value);
    const next = options[index + direction];
    if (!next) return;
    this.select(attr, next.value);
  }

  /** The one way this component writes: set the value, tell the section. */
  select(attr, value) {
    if (!attr) return;
    // Picking the value it already had still confirms the step — the customer
    // has looked at it and decided, which is what the pill is reporting.
    this.confirmed.add(attr.key);
    if (attr.el.value === value) return this.render();
    attr.el.value = value;
    attr.el.dispatchEvent(new Event('change', { bubbles: true }));
    this.render();
  }

  closeSheet(immediate) {
    if (this.sheet.hidden) return;
    this.sheetAttr = null;
    this.sheetSig = null;
    this.scrim.classList.remove('is-open');
    this.sheet.classList.remove('is-open');
    this.root.classList.remove('gbc-sheet-open');
    this.sheet.style.transform = '';
    this.lockScroll(false);

    const finish = () => {
      this.sheet.hidden = true;
      this.scrim.hidden = true;
    };
    window.clearTimeout(this.hideSheetTimer);
    if (immediate || this.reducedMotion()) finish();
    else this.hideSheetTimer = window.setTimeout(finish, 260);

    this.restoreFocus();
  }

  /** Swipe the sheet down to dismiss without changing anything. */
  bindSwipe() {
    let startY = 0;
    let delta = 0;
    let tracking = false;

    const start = (e) => {
      tracking = true;
      delta = 0;
      startY = e.touches[0].clientY;
      this.sheet.style.transition = 'none';
    };
    const move = (e) => {
      if (!tracking) return;
      delta = Math.max(0, e.touches[0].clientY - startY);
      this.sheet.style.transform = `translateY(${delta}px)`;
    };
    const end = () => {
      if (!tracking) return;
      tracking = false;
      this.sheet.style.transition = '';
      if (delta > 80) this.closeSheet();
      else this.sheet.style.transform = '';
    };

    this.grab.addEventListener('touchstart', start, { passive: true });
    this.grab.addEventListener('touchmove', move, { passive: true });
    this.grab.addEventListener('touchend', end);
    this.grab.addEventListener('touchcancel', end);
  }

  /* -------------------------------------------------------------- takeover */

  openWording(trigger) {
    this.closeSheet(true);
    this.rememberFocus(trigger);

    this.renderWordingFormats();
    if (this.wordingText) this.wInput.value = this.wordingText.value;
    this.syncWordingMode();
    this.syncWordingFile();
    this.syncWordingPreview();

    this.takeover.hidden = false;
    this.lockScroll(true);
    this.syncKeyboardInset();
    // Focus synchronously, still inside the tap: iOS only raises the keyboard
    // for a focus call that is part of the user gesture.
    if (!this.wField.hidden) this.wInput.focus({ preventScroll: true });
    else this.takeover.querySelector('.gbc__done').focus({ preventScroll: true });
    requestAnimationFrame(() => this.takeover.classList.add('is-open'));
  }

  closeWording(immediate) {
    if (this.takeover.hidden) return;
    this.takeover.classList.remove('is-open');
    this.lockScroll(false);

    const finish = () => {
      this.takeover.hidden = true;
    };
    window.clearTimeout(this.hideWordingTimer);
    if (immediate || this.reducedMotion()) finish();
    else this.hideWordingTimer = window.setTimeout(finish, 260);

    this.render();
    this.restoreFocus();
  }

  renderWordingFormats() {
    if (!this.wordingFormat) {
      this.wFormats.innerHTML = '';
      return;
    }
    this.wFormats.innerHTML = liveOptions(this.wordingFormat)
      .map((option) => {
        const selected = option.value === this.wordingFormat.value;
        return (
          `<button type="button" role="radio" aria-checked="${selected}" tabindex="${selected ? 0 : -1}"` +
          ` class="gbc__row${selected ? ' is-selected' : ''}" data-value="${escapeHtml(option.value)}">` +
          `<span class="gbc__row-name">${escapeHtml(option.textContent.trim())}</span>` +
          CHECK_SVG +
          '</button>'
        );
      })
      .join('');
  }

  onWordingFormatClick(e) {
    const row = e.target.closest('[data-value]');
    if (!row || !this.wordingFormat) return;
    this.wordingFormat.value = row.getAttribute('data-value');
    this.wordingFormat.dispatchEvent(new Event('change', { bubbles: true }));
    this.renderWordingFormats();
    this.syncWordingMode();
    this.syncWordingPreview();
  }

  syncWordingMode() {
    const format = this.wordingFormat ? this.wordingFormat.value : 'Complete your wording';
    this.wField.hidden = format !== 'Complete your wording';
    this.wFileRow.hidden = format !== 'Upload file' || !this.wordingFile;
  }

  onWordingInput() {
    if (this.wordingText) {
      this.wordingText.value = this.wInput.value;
      this.wordingText.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.syncWordingPreview();
  }

  syncWordingFile() {
    if (!this.wordingFile) return;
    const file = this.wordingFile.files && this.wordingFile.files[0];
    this.wFileName.textContent = file ? file.name : this.strings.no_file_chosen || '';
  }

  /** The preview borrows the mock-up's own paper, ink, typeface and casing. */
  syncWordingPreview() {
    const value = this.wInput.value.trim();
    const placeholder = !value;
    this.wPreview.textContent = placeholder ? this.strings.wording_placeholder || '' : this.wInput.value;
    this.wPreview.classList.toggle('is-placeholder', placeholder);

    const style = this.takeover.style;
    style.setProperty('--gbc-paper', this.cssVar('--pp-paper') || '#fbfbf7');
    style.setProperty('--gbc-ink', this.cssVar('--pp-ink') || '#1b2a52');
    style.setProperty('--gbc-font', this.cssVar('--pp-font') || 'Georgia, serif');
    style.setProperty('--gbc-transform', this.cssVar('--pp-transform') || 'none');
  }

  cssVar(name) {
    if (!this.gallery) return '';
    return (
      this.gallery.style.getPropertyValue(name).trim() ||
      getComputedStyle(this.gallery).getPropertyValue(name).trim()
    );
  }

  /** Keep the text area clear of the on-screen keyboard. */
  syncKeyboardInset() {
    if (this.takeover.hidden || !window.visualViewport) return;
    const vv = window.visualViewport;
    const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    this.takeover.style.setProperty('--gbc-kb', `${inset}px`);
  }

  /* ------------------------------------------------------------- price bar */

  syncPrice() {
    if (!this.priceEl || !this.barAmount) return;
    this.barAmount.textContent = this.priceEl.textContent.trim();
  }

  syncBusy() {
    const busy = this.submitBtn.hasAttribute('disabled');
    this.addBtn.classList.toggle('is-busy', busy);
    this.addBtn.disabled = busy;
    this.addLabel.textContent = busy
      ? this.strings.adding || 'Adding'
      : this.strings.add_to_cart || 'Add to basket';
  }

  syncError() {
    const failed = this.noteEl.classList.contains('is-error');
    this.barError.textContent = failed ? this.noteEl.textContent.trim() : '';
    this.barError.hidden = !failed;
  }

  /**
   * Never disabled. A missing attribute opens its editor and pulses the
   * placeholder; otherwise submit the real form so every downstream step (the
   * bundle builder, the file-upload path, the workshop spec note) runs as-is.
   */
  /** Steps the customer has not been through yet, in ribbon order. */
  outstanding() {
    const keys = this.specAttrs
      .filter((a) => usable(a.scopeEl) && !a.el.disabled && !this.confirmed.has(a.key))
      .map((a) => a.key);
    if (this.wordingFormat && this.missingWording()) keys.push('wording');
    return keys;
  }

  /**
   * Never disabled, and never a dead end. The first tap with steps outstanding
   * turns them red and explains why; the tap after that goes through, because
   * every step already holds a valid default and a prompt must not become a
   * wall. Wording is the one exception — it cannot be printed blank, so it
   * opens its editor rather than just colouring in.
   */
  addToCart() {
    const outstanding = this.outstanding();

    if (outstanding.length && !this.hasNudged) {
      this.hasNudged = true;
      this.markOutstanding(outstanding);

      if (this.missingWording()) {
        const pill = this.lineCard.querySelector('.gbc__pill--required');
        this.revealPill(pill);
        this.openWording(pill);
        return;
      }

      this.showRemaining();
      return;
    }

    // The form is display:none while we are driving it, so a `required` field the
    // customer has not filled would block requestSubmit() with no bubble and no
    // error — the basket button would simply do nothing. Catch it here and send
    // them to the step instead.
    const invalid = this.form.checkValidity() ? null : this.form.querySelector(':invalid');
    if (invalid) {
      const attr = this.specAttrs.find((a) => a.el === invalid);
      if (attr) {
        this.markOutstanding([attr.key]);
        const trigger = this.querySelector(`[data-gbc-open="${CSS.escape(attr.key)}"]`);
        this.revealPill(trigger);
        this.open(attr.key, trigger);
        return;
      }
    }

    this.clearOutstanding();
    this.barError.hidden = true;
    if (this.form.requestSubmit) this.form.requestSubmit(this.submitBtn);
    else this.submitBtn.click();
  }

  markOutstanding(keys) {
    this.outstandingKeys = new Set(keys);
    this.render();
    const first = this.querySelector('.gbc__pill.is-outstanding');
    if (first) this.revealPill(first);
  }

  clearOutstanding() {
    if (!this.outstandingKeys || !this.outstandingKeys.size) return;
    this.outstandingKeys = null;
    this.render();
  }

  showRemaining() {
    this.remaining.hidden = false;
    requestAnimationFrame(() => {
      this.remaining.classList.add('is-open');
      this.remaining.querySelector('[data-gbc-remaining-close]').focus({ preventScroll: true });
    });
  }

  closeRemaining() {
    this.remaining.classList.remove('is-open');
    const finish = () => (this.remaining.hidden = true);
    if (this.reducedMotion()) finish();
    else window.setTimeout(finish, 200);
    const first = this.querySelector('.gbc__pill.is-outstanding');
    if (first) {
      this.revealPill(first);
      first.focus({ preventScroll: true });
    }
  }

  /* ----------------------------------------------------------------- utils */

  onDialogKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!this.takeover.hidden) this.closeWording();
      else this.closeSheet();
      return;
    }
    if (e.key !== 'Tab') return;

    const dialog = this.takeover.hidden ? this.sheet : this.takeover;
    const focusable = Array.prototype.filter.call(
      dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      (el) => !el.disabled && el.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * Focus goes back to the spec-line value that opened the dialog. Remembered
   * by key, not by node: the line is rebuilt on every change, so the original
   * button is usually gone by the time we close.
   */
  /** Bring a pill fully into the ribbon — the tapped one is often half off-screen. */
  revealPill(pill) {
    if (!pill || !pill.scrollIntoView) return;
    pill.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: this.reducedMotion() ? 'auto' : 'smooth'
    });
  }

  rememberFocus(trigger) {
    this.returnFocusTo = trigger || null;
    this.returnFocusKey = trigger ? trigger.getAttribute('data-gbc-open') : null;
  }

  restoreFocus() {
    let target = null;
    if (this.returnFocusKey) {
      target = this.querySelector(`[data-gbc-open="${CSS.escape(this.returnFocusKey)}"]`);
    }
    if (!target && this.returnFocusTo && this.returnFocusTo.isConnected) target = this.returnFocusTo;
    // The "Wording required" placeholder disappears once wording is filled in,
    // so land on the basket button rather than dropping focus to the body.
    if (!target) target = this.addBtn;
    this.returnFocusTo = null;
    this.returnFocusKey = null;
    if (target) target.focus();
  }

  lockScroll(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('mobile-configurator')) {
  customElements.define('mobile-configurator', MobileConfigurator);
}
